import Icon from '@/components/ui/AppIcon';

interface PerformanceTest {
  id: number;
  test: string;
  standard: string;
  result: string;
  significance: string;
}

const performanceTests: PerformanceTest[] = [
  {
    id: 1,
    test: 'Salt Spray Resistance',
    standard: 'ASTM B117',
    result: '1000+ hours no substrate corrosion',
    significance: 'Validates long-term corrosion protection in marine and industrial environments'
  },
  {
    id: 2,
    test: 'Adhesion Cross-Hatch',
    standard: 'ASTM D3359',
    result: '5B rating (no coating removal)',
    significance: 'Confirms superior coating-to-substrate bond strength and durability'
  },
  {
    id: 3,
    test: 'Impact Resistance',
    standard: 'ASTM D2794',
    result: '160 in-lbs direct/reverse',
    significance: 'Demonstrates coating flexibility and resistance to mechanical damage'
  },
  {
    id: 4,
    test: 'Pencil Hardness',
    standard: 'ASTM D3363',
    result: '2H-3H typical',
    significance: 'Indicates coating hardness and scratch resistance for handling durability'
  },
  {
    id: 5,
    test: 'Gloss Retention',
    standard: 'ASTM D523',
    result: '90%+ after 2000h QUV',
    significance: 'Proves UV stability and long-term appearance retention in outdoor exposure'
  },
  {
    id: 6,
    test: 'Chemical Resistance',
    standard: 'ASTM D1308',
    result: 'No effect from common solvents',
    significance: 'Validates coating integrity when exposed to cleaning agents and chemicals'
  }
];

const PerformanceData = () => {
  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-success/10 rounded-full mb-4">
            <Icon name="ChartBarIcon" size={20} variant="outline" className="text-success" />
            <span className="text-sm font-cta font-semibold text-success">
              Validated Performance
            </span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
            Coating Durability Testing
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Comprehensive ASTM testing validates coating performance across critical durability metrics. Every result backed by certified lab data.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-12">
          {performanceTests.map((test) => (
            <div
              key={test.id}
              className="bg-card rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-base border border-border"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-headline font-bold text-foreground mb-1">
                    {test.test}
                  </h3>
                  <div className="text-sm font-accent text-secondary font-medium">
                    {test.standard}
                  </div>
                </div>
                <div className="flex-shrink-0 w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="CheckBadgeIcon" size={24} variant="solid" className="text-success" />
                </div>
              </div>

              <div className="space-y-3">
                <div className="p-3 bg-accent/5 border border-accent/20 rounded-lg">
                  <div className="text-xs font-cta font-semibold text-foreground uppercase tracking-wide mb-1">
                    Test Result
                  </div>
                  <div className="text-sm font-cta font-bold text-accent">
                    {test.result}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-cta font-semibold text-foreground uppercase tracking-wide mb-2">
                    Significance
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {test.significance}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-card rounded-xl shadow-lg border border-border overflow-hidden">
          <div className="bg-primary/5 px-6 py-4 border-b border-border">
            <div className="flex items-center space-x-3">
              <Icon name="DocumentTextIcon" size={24} variant="outline" className="text-primary" />
              <h3 className="text-xl font-headline font-bold text-foreground">
                Testing Certification & Documentation
              </h3>
            </div>
          </div>

          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-headline font-bold text-foreground mb-3">
                  Available Test Reports
                </h4>
                <div className="space-y-2">
                  {[
                    'Complete ASTM test suite results',
                    'Accelerated weathering data (QUV)',
                    'Chemical resistance matrices',
                    'Coating specification sheets',
                    'Material safety data sheets (MSDS)',
                    'Environmental compliance certificates'
                  ].map((item, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <Icon name="DocumentCheckIcon" size={16} variant="solid" className="text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-lg font-headline font-bold text-foreground mb-3">
                  Quality Assurance
                </h4>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-3 bg-success/5 border border-success/20 rounded-lg">
                    <Icon name="ShieldCheckIcon" size={20} variant="solid" className="text-success flex-shrink-0" />
                    <div>
                      <div className="text-sm font-cta font-semibold text-foreground mb-1">
                        ISO 9001:2015 Certified
                      </div>
                      <div className="text-xs text-muted-foreground">
                        All testing performed under certified quality management system
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                    <Icon name="BeakerIcon" size={20} variant="solid" className="text-primary flex-shrink-0" />
                    <div>
                      <div className="text-sm font-cta font-semibold text-foreground mb-1">
                        Third-Party Lab Verification
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Critical tests validated by independent ASTM-certified laboratories
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-border text-center">
              <button className="inline-flex items-center space-x-2 px-8 py-3 bg-primary text-primary-foreground rounded-md font-cta font-bold hover:opacity-90 transition-all duration-base shadow-md">
                <Icon name="ArrowDownTrayIcon" size={20} variant="outline" />
                <span>Download Test Data Package</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PerformanceData;